import { Box, Collapse, FormControlLabel, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import APIDocsLink from "../../../../../components/common/APIDocsLink/APIDocsLink.tsx";
import BriaCheckbox from "../../../../../components/common/BriaCheckbox/BriaCheckbox";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput";
import SurpriseButton from "../../../../../components/common/SurpriseButton/SurpriseButton";
import LinkConstants from "../../../../../constants/LinkConstants.ts";
import { isDnaiOrg } from "../../../../../constants/OrgConstants.ts";
import { useAppStore } from "../../../../../hooks/useStores";
import TgPromptInput from "../../../../NewTG/App/Video/Config/PromptInput/TgPromptInput.tsx";
import TextToImageGenerateButton from "./Button/TextToImageGenerateButton.tsx";
import styles from "./TextToImageSearch.module.scss";
import { Warning } from "@mui/icons-material";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import PromptTextToImageError from "./Error/PromptTextToImageError/PromptTextToImageError.tsx";
import { APPS } from "../../../../../constants/AppsConstants.ts";

const TextToImageSearch = () => {
	const { t, i18n } = useTranslation("translation", { keyPrefix: "playground.textToImage.generator.searchBox" });
	const { textToImageStore, playgroundStore } = useAppStore();
	const disablePrompt = !textToImageStore.config.model_id || textToImageStore.config.restyle_portrait;
	const isNewTg = playgroundStore.selectedConfig == APPS.TG_IMAGE_GENERATION;

	useEffect(() => {
		if (textToImageStore.config.tailored_model_id) {
			textToImageStore.handleConfigChange("prompt_enhancement", false);
		} else if (!textToImageStore.isTextToVector()) {
			textToImageStore.handleConfigChange("prompt_enhancement", true);
		}
		return () => {
			textToImageStore.handleConfigChange("prompt", "");
			textToImageStore.errors = {};
		};
	}, [textToImageStore.config.tailored_model_id]);

	return (
		<Box className={playgroundStore.isText2ImagePopupOpened ? styles.rootModal : styles.root}>
			{/* TODO: Combine the warning into TextToImageError for generate errors - Combined for NewTg*/}
			{isNewTg? null : textToImageStore.IpSignalWarning ? (
				!iframeStore.isIframe() && (
					<Collapse
						in={textToImageStore.IpSignalWarning}
						onExited={() => (textToImageStore.errors.generate = undefined)}
					>
						<Box className={styles.warningMessageContainer}>
							<Warning />
							<Typography className={styles.warningMessage}>
								{i18n.t(`playground.textToImage.errors.ipSignalWarning`)}
							</Typography>
						</Box>
					</Collapse>
				)
			) : (
				<PromptTextToImageError />
			)}
			<Box className={styles.searchBoxWrapper}>
				{textToImageStore.config.tailored_model_id && textToImageStore.config.generation_prefix ? (
					<TgPromptInput
						prefix={
							textToImageStore.config.restyle_portrait ? "" : textToImageStore.config.generation_prefix
						}
						prompt={textToImageStore.config.prompt}
						placeholder={
							textToImageStore.config.restyle_portrait ? t("portraitPlaceholder") : t("placeholder")
						}
						onChange={(newPrompt) => textToImageStore.handleConfigChange("prompt", newPrompt)}
						onEnter={textToImageStore.generateTextToImage}
						disabled={disablePrompt}
						fontSize="16px"
						className={styles.tgPrompt}
						maxRows={4}
					/>
				) : (
					<BriaInput
						value={textToImageStore.config.prompt}
						onChange={(e) => textToImageStore.handleConfigChange("prompt", e.target.value)}
						onEnterPress={textToImageStore.generateTextToImage}
						className={styles.searchBox}
						placeholder={isDnaiOrg() ? t("placeholderDnai") : t("placeholder")}
						InputProps={{
							endAdornment: !isDnaiOrg() ? (
								<SurpriseButton
									textsListType={
										textToImageStore.isTextToVector()
											? "textToVector"
											: textToImageStore.config.model_version === "3.2"
											? "textToImage32"
											: "textToImage2"
									}
									onSurprise={(_e, prompt) => textToImageStore.handleConfigChange("prompt", prompt)}
								/>
							) : undefined,
						}}
						inputProps={{ className: styles.inputBase }}
						rootClass={styles.inputContainer}
						maxRows={4}
						multiline
					/>
				)}

				<TextToImageGenerateButton />
			</Box>
			{!textToImageStore.isTextToVector() && !textToImageStore.config.tailored_model_id && (
				<>
					<FormControlLabel
						disabled={textToImageStore.isImageReferenceEnabled()}
						control={
							<BriaCheckbox
								checked={
									textToImageStore.config?.prompt_enhancement &&
									!textToImageStore.isImageReferenceEnabled()
								}
								onChange={(e) =>
									textToImageStore.handleConfigChange("prompt_enhancement", e.target.checked)
								}
							/>
						}
						label={isDnaiOrg() ? t("enhancedPromptDnai") : t("enhancePrompt")}
						className={styles.controlStyle}
					/>
					<APIDocsLink link={LinkConstants.BRIA_API_PROMPT_ENHANCE} />
				</>
			)}
		</Box>
	);
};

export default observer(TextToImageSearch);
